import React, { useEffect, useState, useCallback } from "react";
import { InfoCircleFilled } from "@ant-design/icons";
import {
  addCourseToBundle,
  CoursesToAdd,
} from "../../../service/BundleService";
import CustomButton from "../../shared/button/CustomButton";
import CustomSearchDropdown from "../../shared/new Form/Fields/CustomSearchDropdown";
import "../../../assets/custom-css/curriculum.css";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../shared/pagination/messages";
import CustomModal from "../../global/CustomModal/CustomModal";
import { DateField } from "../../global/DateField/Datefield";
import { getToday, toEndOfDayISO } from "../../../utility/formatDate";

const ADD_COURSE_INFO_MSG =
  "If you add any course to this curriculum then it will be directly assigned to the existing enrolled employees (if any) of this curriculum and the deadlines will be changed with the new deadline set here.";

const AddCourseModal = ({
  isModalOpen,
  bundleId,
  getCourses,
  handleCloseModal,
  setLoading,
  loading,
}) => {
  const [courseList, setCourseList] = useState([]);
  const [formData, setFormData] = useState({
    courses: [],
    deadline: null,
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const fetchCourseList = useCallback(async () => {
    try {
      const activeUsers = await CoursesToAdd(bundleId);
      const users = activeUsers?.map((user) => ({
        value: user.courseId,
        label: user.title,
      }));
      setCourseList(users || []);
    } catch (error) {
      showErrorMessage("Error fetching courses", "error");
    }
  }, [bundleId]);

  const validate = (data) => {
    const errors = {};

    if (!data.courses || data.courses.length === 0) {
      errors.courses = "Please select at least one course.";
    }

    if (!data.deadline) {
      errors.deadline = "Field should not be Empty.";
    } else if (data.deadline < getToday()) {
      errors.deadline = "Deadline cannot be a past date.";
    }

    return errors;
  };

  const handleFormChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (formErrors[name]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSelectChange = (e) => {
    const selectedValues = e?.target?.value ?? e;
    handleFormChange("courses", selectedValues);
  };

  const handleAdd = async () => {
    setSubmitted(true);

    const errors = validate(formData);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;

    try {
      setLoading(true);

      const payload = {
        bundleId: Number(bundleId),
        courses: formData.courses,
        deadline: toEndOfDayISO(formData.deadline),
        status: "ACTIVE",
      };

      const data = await addCourseToBundle(payload);
      showSuccessMessage(data?.message || "Course added successfully");
      getCourses();
      handleCloseModal();
    } catch (error) {
      const msg = error?.response?.data?.message || "Something went wrong";
      showErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  };
  const resetForm = useCallback(() => {
    setFormData({ courses: [], deadline: null });
    setFormErrors({});
    setSubmitted(false);
  }, []);

  useEffect(() => {
    if (isModalOpen) {
      fetchCourseList();
      resetForm();
    }
  }, [isModalOpen, fetchCourseList, resetForm]);

  return (
    <CustomModal
      title={<span className="add-course-modal-title">Add New Course</span>}
      visible={isModalOpen}
      onClose={handleCloseModal}
      width={520}
    >
      <div className="add-course-modal-container">
        <div className="info-alert-box">
          <div className="info-alert-content">
            <div className="info-alert-header">
              <InfoCircleFilled
                style={{ color: "#1890ff", fontSize: "24px" }}
              />
              <h6 strong className="info-alert-title">
                Adding Course Information
              </h6>
            </div>
            <ul className="info-alert-list">
              <li className="info-alert-item">{ADD_COURSE_INFO_MSG}</li>
            </ul>
          </div>
        </div>

        <div className="form-wrapper">
          <CustomSearchDropdown
            name="courses"
            labelText="Select Course(s) to add"
            placeholder="Select courses"
            multiSelect="multi-select"
            showSearch
            options={courseList}
            isCompulsary={true}
            maxTagCount={1}
            handleSelectChange={handleSelectChange}
            value={formData.courses}
            errorMessage={formErrors.courses}
          />

          <DateField
            field={{
              name: "deadline",
              label: "Set Deadline",
              required: true,
              placeholder: "DD/MM/YYYY",
            }}
            value={formData.deadline}
            onChange={(name, date) => handleFormChange(name, date)}
            onBlur={(name, date) => handleFormChange(name, date)}
            errorMessage={formErrors.deadline}
            minDate={getToday()}
            clearError={(name) =>
              setFormErrors((prev) => ({ ...prev, [name]: "" }))
            }
            openUpward={true}
          />
        </div>
        <div className="form-action-container">
          <CustomButton
            className="custom-btn"
            variant="primary"
            onClick={handleAdd}
            loading={loading}
          >
            Add Courses
          </CustomButton>
        </div>
      </div>
    </CustomModal>
  );
};

export default AddCourseModal;
